<template>
  <div class="swap-page">
    <div v-if="loadingInitial" class="swap-loading">
      <i class="fas fa-spinner fa-spin mr-2"></i> Carregando trocas...
    </div>
    <template v-else>
    <ul class="nav swap-tabs">
      <li class="swap-tab-item">
        <button class="swap-tab" :class="{ active: tab === 'request' }" @click="tab = 'request'">
          <i class="fas fa-paper-plane"></i> <span class="swap-tab-text">Solicitar</span>
        </button>
      </li>
      <li class="swap-tab-item">
        <button class="swap-tab" :class="{ active: tab === 'received' }" @click="tab = 'received'">
          <i class="fas fa-inbox"></i> <span class="swap-tab-text">Recebidas</span>
          <span v-if="received.length" class="swap-tab-badge">{{ received.length }}</span>
        </button>
      </li>
      <li class="swap-tab-item">
        <button class="swap-tab" :class="{ active: tab === 'accepted' }" @click="tab = 'accepted'">
          <i class="fas fa-handshake"></i> <span class="swap-tab-text">Aceitas</span>
          <span v-if="accepted.length" class="swap-tab-badge soft">{{ accepted.length }}</span>
        </button>
      </li>
      <li class="swap-tab-item">
        <button class="swap-tab" :class="{ active: tab === 'mine' }" @click="tab = 'mine'">
          <i class="fas fa-list-ul"></i> <span class="swap-tab-text">Minhas</span>
          <span v-if="mine.length" class="swap-tab-badge soft">{{ mine.length }}</span>
        </button>
      </li>
      <li v-if="isApprover" class="swap-tab-item">
        <button class="swap-tab" :class="{ active: tab === 'approvals' }" @click="tab = 'approvals'">
          <i class="fas fa-user-check"></i> <span class="swap-tab-text">Aprovações</span>
          <span v-if="pendingApprovals.length" class="swap-tab-badge danger">{{ pendingApprovals.length }}</span>
        </button>
      </li>
      <li v-if="isApprover" class="swap-tab-item">
        <button class="swap-tab" :class="{ active: tab === 'direct' }" @click="tab = 'direct'">
          <i class="fas fa-people-arrows"></i> <span class="swap-tab-text">Registrar troca</span>
        </button>
      </li>
    </ul>

    <div class="swap-pane" v-show="tab === 'request'">
      <div class="card-body px-0">
        <div class="swap-step-head"><span class="step-num">1</span> Período</div>
        <div class="row">
          <div class="col-6 col-md-3 mb-2">
            <label class="small text-muted mb-0">Mês</label>
            <select class="form-control" v-model.number="form.month" @change="loadMonth">
              <option v-for="(name, i) in monthNames" :key="i" :value="i + 1">{{ name }}</option>
            </select>
          </div>
          <div class="col-6 col-md-2 mb-2">
            <label class="small text-muted mb-0">Ano</label>
            <select class="form-control" v-model.number="form.year" @change="loadMonth">
              <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
            </select>
          </div>
        </div>

        <div class="swap-step-head mt-2"><span class="step-num">2</span> Escolha as escalas a trocar</div>
        <small class="text-muted d-block mb-2">Somente escalas a partir de hoje podem ser trocadas.</small>
        <div v-if="hasPendingInView" class="swap-hint mb-2">
          <i class="fas fa-info-circle mr-1"></i>
          Escalas marcadas com <span class="sched-pending"><i class="fas fa-clock mr-1"></i>Já solicitada</span>
          já estão em uma troca pendente e não podem ser solicitadas de novo. Acompanhe o andamento na aba <strong>Minhas</strong>.
        </div>
        <div class="swap-grid">
          <div class="swap-col">
            <div class="swap-col-title"><i class="fas fa-user mr-1"></i> Sua escala</div>
            <div v-if="!mySchedules.length" class="swap-empty">Nenhuma escala sua neste mês.</div>
            <div v-else class="sched-list">
              <button type="button" v-for="s in mySchedules" :key="s.id"
                      class="sched-card" :class="{ selected: form.work_schedule_id === s.id, locked: isLocked(s) }"
                      :disabled="isLocked(s)"
                      @click="toggleMine(s.id)">
                <i class="far fa-calendar-check sched-ico"></i>
                <span class="sched-info">
                  <span class="sched-date">{{ dayLabel(s) }}</span>
                  <span class="sched-time">{{ timeLabel(s) }}</span>
                </span>
                <span v-if="isLocked(s)" class="sched-pending"><i class="fas fa-clock mr-1"></i>Já solicitada</span>
                <i v-else-if="form.work_schedule_id === s.id" class="fas fa-check-circle sched-on"></i>
              </button>
            </div>
          </div>

          <div class="swap-arrow"><i class="fas fa-exchange-alt"></i></div>

          <div class="swap-col">
            <div class="swap-col-title"><i class="fas fa-users mr-1"></i> Trocar com</div>
            <v-select :options="colleagues" label="name" :reduce="u => u.id"
                      v-model="form.target_user_id" placeholder="Pesquisar colaborador..."
                      @input="onColleagueChange" />
            <template v-if="form.target_user_id">
              <div v-if="!colleagueSchedules.length" class="swap-empty mt-2">Nenhuma escala do colaborador neste mês.</div>
              <div v-else class="sched-list mt-2">
                <button type="button" v-for="s in colleagueSchedules" :key="s.id"
                        class="sched-card" :class="{ selected: form.target_work_schedule_id === s.id, locked: isLocked(s) }"
                        :disabled="isLocked(s)"
                        @click="toggleTheirs(s.id)">
                  <i class="far fa-calendar sched-ico"></i>
                  <span class="sched-info">
                    <span class="sched-date">{{ dayLabel(s) }}</span>
                    <span class="sched-time">{{ timeLabel(s) }}</span>
                  </span>
                  <span v-if="isLocked(s)" class="sched-pending"><i class="fas fa-clock mr-1"></i>Já solicitada</span>
                  <i v-else-if="form.target_work_schedule_id === s.id" class="fas fa-check-circle sched-on"></i>
                </button>
              </div>
            </template>
            <div v-else class="swap-empty mt-2">Selecione um colaborador para ver as escalas dele.</div>
          </div>
        </div>

        <div v-if="canSubmit" class="summary-bar mt-3">
          <i class="fas fa-info-circle mr-2"></i>
          Você cede <strong>{{ dayLabel(selectedMine) }}</strong> e fica com
          <strong>{{ dayLabel(selectedTheirs) }}</strong> de <strong>{{ selectedColleagueName }}</strong>.
        </div>

        <div v-if="canSubmit" class="swap-times mt-2">
          <button type="button" class="btn btn-link btn-sm px-0" @click="toggleEditTimes">
            <i class="fas mr-1" :class="editTimes ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
            Ajustar horário das escalas (opcional)
          </button>
          <div v-if="editTimes" class="row">
            <div class="col-12 col-md-6 mb-2">
              <div class="swap-col-title small">Sua escala — {{ dayLabel(selectedMine) }}</div>
              <div class="d-flex" style="gap:.5rem;">
                <div class="flex-fill">
                  <label class="small text-muted mb-0">Início</label>
                  <input type="datetime-local" class="form-control" v-model="form.source_shift_start_time">
                </div>
                <div class="flex-fill">
                  <label class="small text-muted mb-0">Fim</label>
                  <input type="datetime-local" class="form-control" v-model="form.source_shift_end_time">
                </div>
              </div>
            </div>
            <div class="col-12 col-md-6 mb-2">
              <div class="swap-col-title small">Escala do colaborador — {{ dayLabel(selectedTheirs) }}</div>
              <div class="d-flex" style="gap:.5rem;">
                <div class="flex-fill">
                  <label class="small text-muted mb-0">Início</label>
                  <input type="datetime-local" class="form-control" v-model="form.target_shift_start_time">
                </div>
                <div class="flex-fill">
                  <label class="small text-muted mb-0">Fim</label>
                  <input type="datetime-local" class="form-control" v-model="form.target_shift_end_time">
                </div>
              </div>
            </div>
            <div class="col-12">
              <small class="text-muted">Deixe em branco para manter o horário atual. Para turno que vira o dia, use a data do dia seguinte no fim. O ajuste só é aplicado quando o gestor aprova.</small>
            </div>
          </div>
        </div>

        <div class="row mt-2">
          <div class="col-12 col-md-8 mb-2">
            <label class="small text-muted mb-0">Motivo (opcional)</label>
            <input type="text" class="form-control" v-model="form.requested_reason" maxlength="1000"
                   placeholder="Ex.: compromisso pessoal">
          </div>
        </div>
        <button class="btn btn-primary" :disabled="loading || !canSubmit" @click="submit">
          <i class="fas fa-paper-plane mr-1"></i> Enviar solicitação
        </button>
      </div>
    </div>

    <div v-if="isApprover" class="swap-pane" v-show="tab === 'direct'">
      <h5 class="pane-title"><i class="fas fa-people-arrows mr-2 text-primary"></i> Registrar troca entre colaboradores</h5>
      <div class="card-body px-0">
        <small class="text-muted d-block mb-2">
          A troca é aplicada imediatamente, sem aceite do colaborador nem aprovação. Somente escalas a partir de hoje.
        </small>

        <div class="swap-step-head"><span class="step-num">1</span> Período</div>
        <div class="row">
          <div class="col-6 col-md-3 mb-2">
            <label class="small text-muted mb-0">Mês</label>
            <select class="form-control" v-model.number="form.month" @change="loadMonth">
              <option v-for="(name, i) in monthNames" :key="i" :value="i + 1">{{ name }}</option>
            </select>
          </div>
          <div class="col-6 col-md-2 mb-2">
            <label class="small text-muted mb-0">Ano</label>
            <select class="form-control" v-model.number="form.year" @change="loadMonth">
              <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
            </select>
          </div>
        </div>

        <div class="swap-step-head mt-2"><span class="step-num">2</span> Escolha os colaboradores e as escalas</div>
        <div class="swap-grid">
          <div class="swap-col">
            <div class="swap-col-title"><i class="fas fa-user mr-1"></i> Colaborador 1</div>
            <v-select :options="directOwners" label="name" :reduce="u => u.id"
                      v-model="directForm.user_a" placeholder="Pesquisar colaborador..."
                      @input="onDirectUserAChange" />
            <template v-if="directForm.user_a">
              <div v-if="!directSchedulesA.length" class="swap-empty mt-2">Nenhuma escala neste mês.</div>
              <div v-else class="sched-list mt-2">
                <button type="button" v-for="s in directSchedulesA" :key="s.id"
                        class="sched-card" :class="{ selected: directForm.work_schedule_id === s.id }"
                        @click="toggleDirectA(s.id)">
                  <i class="far fa-calendar-check sched-ico"></i>
                  <span class="sched-info">
                    <span class="sched-date">{{ dayLabel(s) }}</span>
                    <span class="sched-time">{{ timeLabel(s) }}</span>
                  </span>
                  <i v-if="directForm.work_schedule_id === s.id" class="fas fa-check-circle sched-on"></i>
                </button>
              </div>
            </template>
            <div v-else class="swap-empty mt-2">Selecione o primeiro colaborador.</div>
          </div>

          <div class="swap-arrow"><i class="fas fa-exchange-alt"></i></div>

          <div class="swap-col">
            <div class="swap-col-title"><i class="fas fa-user mr-1"></i> Colaborador 2</div>
            <v-select :options="directOwnersB" label="name" :reduce="u => u.id"
                      v-model="directForm.user_b" placeholder="Pesquisar colaborador..."
                      @input="onDirectUserBChange" />
            <template v-if="directForm.user_b">
              <div v-if="!directSchedulesB.length" class="swap-empty mt-2">Nenhuma escala neste mês.</div>
              <div v-else class="sched-list mt-2">
                <button type="button" v-for="s in directSchedulesB" :key="s.id"
                        class="sched-card" :class="{ selected: directForm.target_work_schedule_id === s.id }"
                        @click="toggleDirectB(s.id)">
                  <i class="far fa-calendar sched-ico"></i>
                  <span class="sched-info">
                    <span class="sched-date">{{ dayLabel(s) }}</span>
                    <span class="sched-time">{{ timeLabel(s) }}</span>
                  </span>
                  <i v-if="directForm.target_work_schedule_id === s.id" class="fas fa-check-circle sched-on"></i>
                </button>
              </div>
            </template>
            <div v-else class="swap-empty mt-2">Selecione o segundo colaborador.</div>
          </div>
        </div>

        <div v-if="canSubmitDirect" class="summary-bar mt-3">
          <i class="fas fa-info-circle mr-2"></i>
          <strong>{{ directSelectedAName }}</strong> assume <strong>{{ dayLabel(directSelectedB) }}</strong>
          e <strong>{{ directSelectedBName }}</strong> assume <strong>{{ dayLabel(directSelectedA) }}</strong>.
        </div>

        <div v-if="canSubmitDirect" class="swap-times mt-2">
          <button type="button" class="btn btn-link btn-sm px-0" @click="toggleDirectEditTimes">
            <i class="fas mr-1" :class="directEditTimes ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
            Ajustar horário das escalas (opcional)
          </button>
          <div v-if="directEditTimes" class="row">
            <div class="col-12 col-md-6 mb-2">
              <div class="swap-col-title small">{{ directSelectedAName }} — {{ dayLabel(directSelectedA) }}</div>
              <div class="d-flex" style="gap:.5rem;">
                <div class="flex-fill">
                  <label class="small text-muted mb-0">Início</label>
                  <input type="datetime-local" class="form-control" v-model="directForm.source_shift_start_time">
                </div>
                <div class="flex-fill">
                  <label class="small text-muted mb-0">Fim</label>
                  <input type="datetime-local" class="form-control" v-model="directForm.source_shift_end_time">
                </div>
              </div>
            </div>
            <div class="col-12 col-md-6 mb-2">
              <div class="swap-col-title small">{{ directSelectedBName }} — {{ dayLabel(directSelectedB) }}</div>
              <div class="d-flex" style="gap:.5rem;">
                <div class="flex-fill">
                  <label class="small text-muted mb-0">Início</label>
                  <input type="datetime-local" class="form-control" v-model="directForm.target_shift_start_time">
                </div>
                <div class="flex-fill">
                  <label class="small text-muted mb-0">Fim</label>
                  <input type="datetime-local" class="form-control" v-model="directForm.target_shift_end_time">
                </div>
              </div>
            </div>
            <div class="col-12">
              <small class="text-muted">Deixe em branco para manter o horário atual. Para turno que vira o dia, use a data do dia seguinte no fim.</small>
            </div>
          </div>
        </div>

        <div class="row mt-2">
          <div class="col-12 col-md-8 mb-2">
            <label class="small text-muted mb-0">Motivo (opcional)</label>
            <input type="text" class="form-control" v-model="directForm.requested_reason" maxlength="1000"
                   placeholder="Ex.: acordo entre a equipe">
          </div>
        </div>

        <div class="custom-control custom-checkbox mb-3">
          <input type="checkbox" class="custom-control-input" id="direct-notify" v-model="directForm.notify">
          <label class="custom-control-label" for="direct-notify">Avisar os colaboradores por e-mail</label>
        </div>

        <button class="btn btn-primary" :disabled="loading || !canSubmitDirect" @click="submitDirect">
          <i class="fas fa-people-arrows mr-1"></i> Efetivar troca
        </button>
      </div>
    </div>

    <div class="swap-pane" v-show="tab === 'received'">
      <h5 class="pane-title"><i class="fas fa-inbox mr-2 text-warning"></i> Trocas recebidas</h5>
      <div class="card-body px-0">
        <div class="mb-3" style="max-width: 260px;">
          <select class="form-control form-control-sm" v-model="receivedAlocationFilter">
            <option value="all">Todas as alocações</option>
            <option v-for="a in alocations" :key="a.id" :value="a.id">{{ a.name }}</option>
          </select>
        </div>

        <div v-if="receivedLoading" class="text-center py-3 text-muted">
          <i class="fas fa-spinner fa-spin mr-2"></i> Carregando...
        </div>
        <div v-else-if="!receivedList.length" class="swap-empty text-center py-3">
          <i class="far fa-check-circle d-block mb-1" style="font-size:1.4rem;"></i>
          Nenhuma troca aguardando sua resposta.
        </div>
        <template v-else>
          <div v-for="r in receivedList" :key="r.id" class="recv-card">
            <div class="recv-info">
              <div><strong>{{ r.requested_by && r.requested_by.name }}</strong> quer trocar</div>
              <div class="text-muted small">
                a escala <strong>{{ dutyDay(r.work_schedule) }}</strong>
                <span class="swap-range">({{ shiftRangeLabel(r.work_schedule) }})</span>
                pela sua
                <strong>{{ dutyDay(r.target_work_schedule) }}</strong>
                <span class="swap-range">({{ shiftRangeLabel(r.target_work_schedule) }})</span>
                <span v-if="r.requested_reason"> — “{{ r.requested_reason }}”</span>
              </div>
            </div>
            <div class="recv-actions">
              <button class="btn btn-sm btn-success mr-1" :disabled="loading" @click="accept(r.id)">
                <i class="fas fa-check mr-1"></i> Aceitar
              </button>
              <button class="btn btn-sm btn-outline-danger" :disabled="loading" @click="decline(r.id)">
                <i class="fas fa-times mr-1"></i> Recusar
              </button>
            </div>
          </div>
          <swap-pagination :page="receivedMeta.page" :pages="receivedMeta.pages" :total="receivedMeta.count"
                           :per-page="10" :disabled="receivedLoading"
                           @change="p => goToTabPage('received', p)" />
        </template>
      </div>
    </div>

    <div class="swap-pane" v-show="tab === 'accepted'">
      <h5 class="pane-title"><i class="fas fa-handshake mr-2 text-success"></i> Trocas que você aceitou</h5>
      <div class="card-body px-0">
        <div v-if="acceptedLoading" class="text-center py-3 text-muted">
          <i class="fas fa-spinner fa-spin mr-2"></i> Carregando...
        </div>
        <div v-else-if="!acceptedList.length" class="swap-empty text-center py-3">Nenhuma troca aceita aguardando aprovação.</div>
        <template v-else>
          <div v-for="r in acceptedList" :key="r.id" class="recv-card">
            <div class="recv-info">
              <div><strong>{{ r.requested_by && r.requested_by.name }}</strong> — troca aceita</div>
              <div class="text-muted small">
                a escala <strong>{{ dutyDay(r.work_schedule) }}</strong>
                <span class="swap-range">({{ shiftRangeLabel(r.work_schedule) }})</span>
                pela sua
                <strong>{{ dutyDay(r.target_work_schedule) }}</strong>
                <span class="swap-range">({{ shiftRangeLabel(r.target_work_schedule) }})</span>
              </div>
            </div>
            <span class="badge badge-info align-self-center">Aguardando aprovação do supervisor</span>
          </div>
          <swap-pagination :page="acceptedMeta.page" :pages="acceptedMeta.pages" :total="acceptedMeta.count"
                           :per-page="10" :disabled="acceptedLoading"
                           @change="p => goToTabPage('accepted', p)" />
        </template>
      </div>
    </div>

    <div class="swap-pane" v-show="tab === 'mine'">
      <h5 class="pane-title"><i class="fas fa-paper-plane mr-2 text-primary"></i> Minhas solicitações</h5>
      <div class="card-body px-0">
        <div v-if="mineLoading" class="text-center py-3 text-muted">
          <i class="fas fa-spinner fa-spin mr-2"></i> Carregando...
        </div>
        <div v-else-if="!mineList.length" class="swap-empty text-center py-3">Você não tem solicitações.</div>
        <template v-else>
          <div class="table-responsive">
            <table class="table table-hover mb-0">
              <thead>
                <tr><th>Colaborador</th><th>Sua escala</th><th>Escala do colaborador</th><th>Status</th></tr>
              </thead>
              <tbody>
                <tr v-for="r in mineList" :key="r.id">
                  <td>
                    {{ mineOtherName(r) }}
                    <strong><div v-if="!isMineRequester(r)" class="swap-range">Solicitou a troca com você</div></strong>
                  </td>
                  <td>{{ dutyDay(mineOwnSchedule(r)) }}<div class="swap-range">{{ shiftRangeLabel(mineOwnSchedule(r)) }}</div></td>
                  <td>{{ dutyDay(mineOtherSchedule(r)) }}<div class="swap-range">{{ shiftRangeLabel(mineOtherSchedule(r)) }}</div></td>
                  <td><span class="badge" :class="statusClass(r.status)">{{ statusLabel(r.status) }}</span></td>
                </tr>
              </tbody>

            </table>
          </div>
          <swap-pagination :page="mineMeta.page" :pages="mineMeta.pages" :total="mineMeta.count"
                           :per-page="10" :disabled="mineLoading"
                           @change="p => goToTabPage('mine', p)" />
        </template>
      </div>
    </div>

    <div v-if="isApprover" class="swap-pane" v-show="tab === 'approvals'">
      <div class="pane-title d-flex justify-content-between align-items-center">
        <span><i class="fas fa-user-check mr-2 text-danger"></i> Aprovações pendentes</span>
        <button class="btn btn-sm btn-outline-secondary" :disabled="loading" @click="fetchSwaps">
          <i class="fas fa-sync-alt mr-1"></i> Atualizar
        </button>
      </div>
      <div class="btn-group btn-group-sm mb-3" role="group">
        <button type="button" class="btn" :class="approvalsView === 'pending' ? 'btn-primary' : 'btn-outline-primary'"
                @click="approvalsView = 'pending'">
          Pendentes <span v-if="pendingApprovals.length" class="badge badge-light" style="color: #000;">{{ pendingApprovals.length }}</span>
        </button>
        <button type="button" class="btn" :class="approvalsView === 'history' ? 'btn-primary' : 'btn-outline-primary'"
                @click="approvalsView = 'history'">
          Histórico
        </button>
      </div>
      <div v-show="approvalsView === 'pending'" class="card-body px-0">
        <div v-if="approvalsLoading" class="text-center py-3 text-muted">
          <i class="fas fa-spinner fa-spin mr-2"></i> Carregando...
        </div>
        <div v-else-if="!approvalsList.length" class="swap-empty text-center py-3">
          <i class="fas fa-check-circle text-success d-block mb-1" style="font-size:1.4rem;"></i>
          Nenhuma troca aguardando aprovação.
        </div>
        <template v-else>
        <div class="table-responsive">
          <table class="table table-hover mb-0">
            <thead>
              <tr>
                <th>Solicitante</th><th>Data e Hora da solicitação</th><th>Escala dele</th><th>Colaborador</th><th>Escala do colaborador</th>
                <th>Motivo</th><th class="text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="r in approvalsList">
              <tr :key="r.id">
                <td>{{ r.requested_by && r.requested_by.name }}</td>
                <td>{{ r.created_at ? formatDateTime(r.created_at) : '—' }}</td>
                <td>{{ dutyDay(r.work_schedule) }}<div class="swap-range">{{ shiftRangeLabel(r.work_schedule) }}</div></td>
                <td>{{ (r.target_user && r.target_user.name) || '—' }}</td>
                <td>{{ dutyDay(r.target_work_schedule) }}<div class="swap-range">{{ shiftRangeLabel(r.target_work_schedule) }}</div></td>
                <td>{{ r.requested_reason || '—' }}</td>
                <td class="text-center">
                  <div class="swap-actions-cell">
                    <button class="btn btn-sm btn-outline-secondary" :disabled="loading" @click="toggleApprovalEdit(r)">
                      <i class="fas fa-clock"></i> Horário
                    </button>
                    <button class="btn btn-sm btn-outline-success" :disabled="loading" @click="approve(r.id)">
                      <i class="fas fa-check"></i> Aprovar
                    </button>
                    <button class="btn btn-sm btn-outline-danger" :disabled="loading" @click="reject(r.id)">
                      <i class="fas fa-times"></i> Reprovar
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="approvalEditId === r.id && approvalEdit[r.id]" :key="r.id + '-edit'" class="swap-approval-edit">
                <td colspan="6">
                  <div class="row">
                    <div class="col-12 col-md-6 mb-2">
                      <div class="swap-col-title small">{{ r.requested_by && r.requested_by.name }} — {{ dutyDay(r.work_schedule) }}</div>
                      <div class="d-flex" style="gap:.5rem;">
                        <div class="flex-fill">
                          <label class="small text-muted mb-0">Início</label>
                          <input type="datetime-local" class="form-control" v-model="approvalEdit[r.id].source_shift_start_time">
                        </div>
                        <div class="flex-fill">
                          <label class="small text-muted mb-0">Fim</label>
                          <input type="datetime-local" class="form-control" v-model="approvalEdit[r.id].source_shift_end_time">
                        </div>
                      </div>
                    </div>
                    <div class="col-12 col-md-6 mb-2">
                      <div class="swap-col-title small">{{ (r.target_user && r.target_user.name) || '—' }} — {{ dutyDay(r.target_work_schedule) }}</div>
                      <div class="d-flex" style="gap:.5rem;">
                        <div class="flex-fill">
                          <label class="small text-muted mb-0">Início</label>
                          <input type="datetime-local" class="form-control" v-model="approvalEdit[r.id].target_shift_start_time">
                        </div>
                        <div class="flex-fill">
                          <label class="small text-muted mb-0">Fim</label>
                          <input type="datetime-local" class="form-control" v-model="approvalEdit[r.id].target_shift_end_time">
                        </div>
                      </div>
                    </div>
                    <div class="col-12"><small class="text-muted">Deixe em branco para manter o horário atual. Os ajustes são aplicados ao aprovar.</small></div>
                  </div>
                </td>
              </tr>
              </template>
            </tbody>
          </table>
        </div>
        <swap-pagination :page="approvalsMeta.page" :pages="approvalsMeta.pages" :total="approvalsMeta.count"
                         :per-page="10" :disabled="approvalsLoading"
                         @change="p => goToTabPage('approvals', p)" />
        </template>
      </div>
      <div v-show="approvalsView === 'history'" class="card-body px-0">
        <div class="swap-history-toolbar">
          <span class="text-muted small">{{ historyMeta.total }} troca(s)</span>
          <button v-if="historyFiltersActive" type="button" class="btn btn-sm btn-link px-0"
                  @click="clearHistoryFilters">
            <i class="fas fa-times mr-1"></i> Limpar filtros
          </button>
        </div>
        <div class="table-responsive">
          <table class="table table-hover mb-0 swap-history-table">
            <thead>
              <tr>
                <th>Solicitante</th><th>Data e Hora da solicitação</th><th>Escala dele</th><th>Colaborador</th><th>Escala do colaborador</th>
                <th>Status</th><th>Motivo</th><th>Decidido por</th><th>Data e Hora</th>
              </tr>
              <tr class="swap-filter-row">
                <th>
                  <input type="text" class="form-control form-control-sm" v-model="historyFilters.requester"
                         placeholder="Filtrar...">
                </th>
                <th></th>
                <th>
                  <input type="date" class="form-control form-control-sm swap-date-input"
                         v-model="historyFilters.source_shift_date">
                </th>
                <th>
                  <input type="text" class="form-control form-control-sm" v-model="historyFilters.colleague"
                         placeholder="Filtrar...">
                </th>
                <th>
                  <input type="date" class="form-control form-control-sm swap-date-input"
                         v-model="historyFilters.target_shift_date">
                </th>
                <th>
                  <select class="form-control form-control-sm" v-model="historyFilters.status">
                    <option value="">Todos</option>
                    <option value="approved">Aprovada</option>
                    <option value="rejected">Reprovada</option>
                  </select>
                </th>
                <th>
                  <input type="text" class="form-control form-control-sm" v-model="historyFilters.reason"
                         placeholder="Filtrar...">
                </th>
                <th>
                  <input type="text" class="form-control form-control-sm" v-model="historyFilters.approver"
                         placeholder="Filtrar...">
                </th>
                <th>
                  <input type="date" class="form-control form-control-sm swap-date-input"
                         v-model="historyFilters.decided_date">
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="historyLoading">
                <td colspan="9" class="text-center py-3 text-muted">
                  <i class="fas fa-spinner fa-spin mr-2"></i> Carregando histórico...
                </td>
              </tr>
              <tr v-else-if="!history.length">
                <td colspan="9" class="text-center py-3 text-muted">
                  {{ historyFiltersActive ? 'Nenhuma troca corresponde aos filtros.' : 'Nenhuma troca decidida ainda.' }}
                </td>
              </tr>
              <tr v-for="r in history" v-else :key="r.id">
                <td>{{ r.requested_by && r.requested_by.name }}</td>
                <td>{{ r.created_at ? formatDateTime(r.created_at) : '—' }}</td>
                <td>{{ dutyDay(r.work_schedule) }}<div class="swap-range">{{ shiftRangeLabel(r.work_schedule) }}</div></td>
                <td>{{ (r.target_user && r.target_user.name) || '—' }}</td>
                <td>{{ dutyDay(r.target_work_schedule) }}<div class="swap-range">{{ shiftRangeLabel(r.target_work_schedule) }}</div></td>
                <td><span class="badge" :class="statusClass(r.status)">{{ statusLabel(r.status) }}</span></td>
                <td>{{ r.approver_justification || '—' }}</td>
                <td>{{ (r.approver && r.approver.name) || '—' }}</td>
                <td>{{ r.updated_at ? formatDateTime(r.updated_at) : '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="historyMeta.last_page > 1" class="d-flex justify-content-between align-items-center mt-2">
          <button class="btn btn-sm btn-outline-secondary" :disabled="historyMeta.current_page <= 1 || historyLoading"
                  @click="fetchHistory(historyMeta.current_page - 1)">
            <i class="fas fa-chevron-left mr-1"></i> Anterior
          </button>
          <span class="text-muted small">{{ historyMeta.current_page }} / {{ historyMeta.last_page }}</span>
          <button class="btn btn-sm btn-outline-secondary" :disabled="historyMeta.current_page >= historyMeta.last_page || historyLoading"
                  @click="fetchHistory(historyMeta.current_page + 1)">
            Próxima <i class="fas fa-chevron-right ml-1"></i>
          </button>
        </div>
      </div>
    </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import swal from 'sweetalert';
import SwapPagination from '@/components/SwapPagination';

export default {
  name: 'SwapPanel',
  components: { SwapPagination },
  data() {
    const now = new Date();
    return {
      loading: false,
      loadingInitial: true,
      tab: 'request',
      defaultTabSet: false,
      form: {
        year: now.getFullYear(),
        month: now.getMonth() + 1,
        work_schedule_id: '',
        target_user_id: null,
        target_work_schedule_id: '',
        requested_reason: '',
        source_shift_start_time: '',
        source_shift_end_time: '',
        target_shift_start_time: '',
        target_shift_end_time: '',
      },
      receivedAlocationFilter: 'all',
      alocations: [],
      editTimes: false,
      directForm: {
        user_a: null,
        user_b: null,
        work_schedule_id: '',
        target_work_schedule_id: '',
        requested_reason: '',
        source_shift_start_time: '',
        source_shift_end_time: '',
        target_shift_start_time: '',
        target_shift_end_time: '',
        notify: false,
      },
      directEditTimes: false,
      approvalEdit: {},
      approvalEditId: null,
      approvalsView: 'pending',
      historyFilters: {
        requester: '', source_shift_date: '', colleague: '', target_shift_date: '',
        status: '', reason: '', approver: '', decided_date: '',
      },
      history: [],
      historyMeta: { current_page: 1, last_page: 1, total: 0 },
      historyLoading: false,
      historyLoaded: false,
      historyDebounce: null,
      receivedList: [],
      receivedMeta: { page: 1, pages: 1, count: 0 },
      receivedLoading: false,
      receivedLoaded: false,
      acceptedList: [],
      acceptedMeta: { page: 1, pages: 1, count: 0 },
      acceptedLoading: false,
      acceptedLoaded: false,
      mineList: [],
      mineMeta: { page: 1, pages: 1, count: 0 },
      mineLoading: false,
      mineLoaded: false,
      approvalsList: [],
      approvalsMeta: { page: 1, pages: 1, count: 0 },
      approvalsLoading: false,
      approvalsLoaded: false,
      myId: null,
      monthSchedules: [],
      swaps: [],
      monthNames: ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
        'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'],
    };
  },
  computed: {
    isApprover() {
      const user = this.$store.state.user;
      return !!(user && Number(user.is_admin) !== 3);
    },
    me() {
      return this.myId || (this.$store.state.user && this.$store.state.user.id) || null;
    },
    yearOptions() {
      const y = new Date().getFullYear();
      return [y - 1, y, y + 1];
    },
    canSubmit() {
      return this.form.work_schedule_id && this.form.target_work_schedule_id
        && !this.lockedScheduleIds.has(Number(this.form.work_schedule_id))
        && !this.lockedScheduleIds.has(Number(this.form.target_work_schedule_id));
    },
    lockedScheduleIds() {
      const ids = new Set();
      this.swaps.forEach((r) => {
        if (!['pending_peer', 'pending_approval'].includes(r.status)) return;
        if (Number(r.requested_by_user_id) !== Number(this.me)) return;
        const a = r.work_schedule && r.work_schedule.id;
        const b = r.target_work_schedule && r.target_work_schedule.id;
        if (a) ids.add(Number(a));
        if (b) ids.add(Number(b));
      });
      return ids;
    },
    hasPendingInView() {
      return this.mySchedules.some(s => this.isLocked(s))
        || this.colleagueSchedules.some(s => this.isLocked(s));
    },
    received() {
      if (!this.me) return [];
      let list = this.swaps.filter(r => Number(r.target_user_id) === Number(this.me) && r.status === 'pending_peer');
      if (this.receivedAlocationFilter !== 'all') {
        list = list.filter(r => Number(r.requested_by && r.requested_by.alocation_id) === Number(this.receivedAlocationFilter));
      }
      return list;
    },
    accepted() {
      if (!this.me) return [];
      return this.swaps.filter(r => Number(r.target_user_id) === Number(this.me) && r.status === 'pending_approval');
    },
    mine() {
      if (!this.me) return [];
      return this.swaps.filter(r => (Number(r.requested_by_user_id) === Number(this.me)
          || Number(r.target_user_id) === Number(this.me))
        && (r.work_schedule || r.target_work_schedule));
    },
    canSelfApprove() {
      const u = this.$store.state.user;
      return !!(u && [1, 2].includes(Number(u.is_admin)));
    },
    pendingApprovals() {
      return this.swaps.filter(r => r.status === 'pending_approval'
        && (this.canSelfApprove || Number(r.requested_by_user_id) !== Number(this.me))
        && !this.isScheduleMissing(r));
    },
    historyFiltersActive() {
      return Object.values(this.historyFilters).some(v => v !== '' && v !== null);
    },
    swappableSchedules() {
      const today = moment().startOf('day');
      return this.monthSchedules.filter(s => s.duty_day && moment(s.duty_day).isSameOrAfter(today, 'day'));
    },
    directSwappableSchedules() {
      return this.monthSchedules.filter(s => s.duty_day);
    },
    mySchedules() {
      return this.swappableSchedules.filter(s => Number(this.ownerId(s)) === Number(this.me));
    },
    colleagues() {
      const map = {};
      this.swappableSchedules.forEach((s) => {
        const id = this.ownerId(s);
        if (!id || Number(id) === Number(this.me)) return;
        if (!map[id]) map[id] = { id, name: this.ownerName(s) };
      });
      return Object.values(map).sort((a, b) => String(a.name).localeCompare(String(b.name)));
    },
    colleagueSchedules() {
      if (!this.form.target_user_id) return [];
      return this.swappableSchedules.filter(s => Number(this.ownerId(s)) === Number(this.form.target_user_id));
    },
    selectedMine() {
      return this.mySchedules.find(s => s.id === this.form.work_schedule_id) || null;
    },
    selectedTheirs() {
      return this.colleagueSchedules.find(s => s.id === this.form.target_work_schedule_id) || null;
    },
    selectedColleagueName() {
      const c = this.colleagues.find(u => Number(u.id) === Number(this.form.target_user_id));
      return c ? c.name : '';
    },
    directOwners() {
      const map = {};
      this.directSwappableSchedules.forEach((s) => {
        const id = this.ownerId(s);
        if (!id) return;
        if (!map[id]) map[id] = { id, name: this.ownerName(s) };
      });
      return Object.values(map).sort((a, b) => String(a.name).localeCompare(String(b.name)));
    },
    directOwnersB() {
      return this.directOwners.filter(u => Number(u.id) !== Number(this.directForm.user_a));
    },
    directSchedulesA() {
      if (!this.directForm.user_a) return [];
      return this.directSwappableSchedules.filter(s => Number(this.ownerId(s)) === Number(this.directForm.user_a));
    },
    directSchedulesB() {
      if (!this.directForm.user_b) return [];
      return this.directSwappableSchedules.filter(s => Number(this.ownerId(s)) === Number(this.directForm.user_b));
    },
    directSelectedA() {
      return this.directSchedulesA.find(s => s.id === this.directForm.work_schedule_id) || null;
    },
    directSelectedB() {
      return this.directSchedulesB.find(s => s.id === this.directForm.target_work_schedule_id) || null;
    },
    directSelectedAName() {
      const c = this.directOwners.find(u => Number(u.id) === Number(this.directForm.user_a));
      return c ? c.name : '';
    },
    directSelectedBName() {
      const c = this.directOwners.find(u => Number(u.id) === Number(this.directForm.user_b));
      return c ? c.name : '';
    },
    canSubmitDirect() {
      return !!this.directForm.work_schedule_id && !!this.directForm.target_work_schedule_id
        && Number(this.directForm.user_a) !== Number(this.directForm.user_b);
    },
  },
  watch: {
    tab(newTab) {
      if (newTab === 'received' && !this.receivedLoaded) this.fetchTabList('received');
      if (newTab === 'accepted' && !this.acceptedLoaded) this.fetchTabList('accepted');
      if (newTab === 'mine' && !this.mineLoaded) this.fetchTabList('mine');
      if (newTab === 'approvals' && this.approvalsView === 'pending' && !this.approvalsLoaded) this.fetchTabList('approvals');
    },
    receivedAlocationFilter() {
      this.receivedMeta.page = 1;
      this.fetchTabList('received');
    },
    approvalsView(view) {
      if (view === 'history' && !this.historyLoaded) this.fetchHistory(1);
      if (view === 'pending' && !this.approvalsLoaded) this.fetchTabList('approvals');
    },
    'historyFilters.status'() { if (this.approvalsView === 'history') this.fetchHistory(1); },
    'historyFilters.source_shift_date'() { if (this.approvalsView === 'history') this.fetchHistory(1); },
    'historyFilters.target_shift_date'() { if (this.approvalsView === 'history') this.fetchHistory(1); },
    'historyFilters.decided_date'() { if (this.approvalsView === 'history') this.fetchHistory(1); },
    'historyFilters.requester'() { if (this.approvalsView === 'history') this.queueHistoryFetch(); },
    'historyFilters.colleague'() { if (this.approvalsView === 'history') this.queueHistoryFetch(); },
    'historyFilters.approver'() { if (this.approvalsView === 'history') this.queueHistoryFetch(); },
    'historyFilters.reason'() { if (this.approvalsView === 'history') this.queueHistoryFetch(); },
  },
  mounted() {
    this.loadMe().then(() => {
      this.loadMonth();
      this.fetchSwaps();
    });
    this.loadAlocations();
  },
  methods: {
    api(path) {
      return this.$store.state.api + path;
    },
    loadAlocations() {
      axios.get(this.api('alocations'))
        .then(r => { this.alocations = Array.isArray(r.data) ? r.data : (r.data.data || []); })
        .catch(() => { this.alocations = []; });
    },
    loadMe() {
      return axios.post(this.api('me'), { token: this.$store.state.token })
        .then(r => { this.myId = (r.data && r.data.id) || null; })
        .catch(() => { this.myId = (this.$store.state.user && this.$store.state.user.id) || null; });
    },
    dutyDay(s) {
      const base = s && (s.shift_start_time || s.duty_day);
      return base ? moment(base).format('DD/MM/YYYY') : '—';
    },
    formatDateTime(v) {
      const m = moment(v);
      return m.isValid() ? m.format('DD/MM/YYYY HH:mm') : '—';
    },
    dayLabel(s) {
      const base = s && (s.shift_start_time || s.duty_day);
      return base ? moment(base).format('DD/MM') : '—';
    },
    timeLabel(s) {
      if (!s) return '';
      const start = s.shift_start_time ? moment(s.shift_start_time).format('HH:mm') : '';
      const end = s.shift_end_time ? moment(s.shift_end_time).format('HH:mm') : '';
      return start && end ? `${start} – ${end}` : 'Dia inteiro';
    },
    shiftRangeLabel(s) {
      if (!s || !s.shift_start_time || !s.shift_end_time) return 'Dia inteiro';
      const start = moment(s.shift_start_time);
      const end = moment(s.shift_end_time);
      if (!start.isValid() || !end.isValid()) return 'Dia inteiro';
      return start.isSame(end, 'day')
        ? `${start.format('DD/MM HH:mm')} – ${end.format('HH:mm')}`
        : `${start.format('DD/MM HH:mm')} → ${end.format('DD/MM HH:mm')}`;
    },
    toInputDt(v) {
      if (!v) return '';
      const m = moment(v);
      return m.isValid() ? m.format('YYYY-MM-DDTHH:mm') : '';
    },
    statusLabel(s) {
      return {
        pending_peer: 'Aguardando colaborador',
        pending_approval: 'Aguardando aprovação',
        approved: 'Aprovada',
        rejected: 'Recusada/Reprovada',
      }[s] || s;
    },
    statusClass(s) {
      return {
        pending_peer: 'badge-warning',
        pending_approval: 'badge-info',
        approved: 'badge-success',
        rejected: 'badge-danger',
      }[s] || 'badge-secondary';
    },
    ownerId(s) {
      return s.swapped_user_id || s.user_id;
    },
    ownerName(s) {
      return s.swapped_user_name || s.user_name || '';
    },
    loadMonth() {
      this.form.work_schedule_id = '';
      this.form.target_user_id = null;
      this.form.target_work_schedule_id = '';
      axios.get(this.api('work_schedules'), {
        params: { year: this.form.year, month: this.form.month, include_user_names: 1 },
      })
        .then(r => { this.monthSchedules = Array.isArray(r.data) ? r.data : (r.data.data || []); })
        .catch(() => { this.monthSchedules = []; });
    },
    onColleagueChange() {
      this.form.target_work_schedule_id = '';
    },
    isLocked(s) {
      return !!(s && this.lockedScheduleIds.has(Number(s.id)));
    },
    isScheduleMissing(r) {
      // A escala referenciada (origem ou destino) não existe mais: o backend manda a
      // relação como null quando o WorkSchedule foi removido, ou preenchida com
      // deleted_at quando foi soft-deleted (ex.: origem apagada ao aprovar a troca).
      return !!(r && (
        (r.work_schedule_id && (!r.work_schedule || r.work_schedule.deleted_at))
        || (r.target_work_schedule_id && (!r.target_work_schedule || r.target_work_schedule.deleted_at))
      ));
    },
    toggleMine(id) {
      if (this.lockedScheduleIds.has(Number(id))) return;
      this.form.work_schedule_id = this.form.work_schedule_id === id ? '' : id;
    },
    toggleTheirs(id) {
      if (this.lockedScheduleIds.has(Number(id))) return;
      this.form.target_work_schedule_id = this.form.target_work_schedule_id === id ? '' : id;
    },
    fetchSwaps() {
      axios.get(this.api('work_schedule_swap_requests'))
        .then(r => {
          this.swaps = (r.data && r.data.data) || [];
          if (!this.defaultTabSet) {
            this.defaultTabSet = true;
            const requested = this.$route.query.tab;
            if (requested === 'received' || (requested === 'approvals' && this.isApprover)) this.tab = requested;
            else if (this.isApprover && this.pendingApprovals.length) this.tab = 'approvals';
            else if (this.received.length) this.tab = 'received';
          }
          if (this.approvalsView === 'history' && this.historyLoaded) {
            this.fetchHistory(this.historyMeta.current_page);
          }
          this.refreshTabLists();
        })
        .catch(() => { this.swaps = []; })
        .finally(() => { this.loadingInitial = false; });
    },
    clearHistoryFilters() {
      this.historyFilters = {
        requester: '', source_shift_date: '', colleague: '', target_shift_date: '',
        status: '', reason: '', approver: '', decided_date: '',
      };
      this.fetchHistory(1);
    },
    fetchHistory(page) {
      this.historyLoading = true;
      const f = this.historyFilters;
      const params = { page: page || 1 };
      if (f.status) params.status = f.status;
      if (f.requester) params.requester = f.requester.trim();
      if (f.colleague) params.colleague = f.colleague.trim();
      if (f.approver) params.approver = f.approver.trim();
      if (f.reason) params.reason = f.reason.trim();
      if (f.source_shift_date) params.source_shift_date = f.source_shift_date;
      if (f.target_shift_date) params.target_shift_date = f.target_shift_date;
      if (f.decided_date) params.decided_date = f.decided_date;
      axios.get(this.api('work_schedule_swap_requests/history'), { params })
        .then((r) => {
          const p = (r.data && r.data.data) || {};
          this.history = p.data || [];
          this.historyMeta = {
            current_page: p.current_page || 1,
            last_page: p.last_page || 1,
            total: p.total || 0,
          };
          this.historyLoaded = true;
        })
        .catch(() => {
          this.history = [];
          this.historyMeta = { current_page: 1, last_page: 1, total: 0 };
        })
        .finally(() => { this.historyLoading = false; });
    },
    queueHistoryFetch() {
      if (this.historyDebounce) clearTimeout(this.historyDebounce);
      this.historyDebounce = setTimeout(() => { this.fetchHistory(1); }, 350);
    },
    fetchTabList(view) {
      const metaKey = `${view}Meta`;
      const listKey = `${view}List`;
      const loadingKey = `${view}Loading`;
      const page = this[metaKey].page || 1;
      this[loadingKey] = true;
      const params = { paginated: 1, limit: 10, page, view };
      if (view === 'received' && this.receivedAlocationFilter !== 'all') {
        params.alocation_id = this.receivedAlocationFilter;
      }
      axios.get(this.api('work_schedule_swap_requests/tab'), { params })
        .then((r) => {
          const p = (r.data && r.data.data) || {};
          this[listKey] = p.data || [];
          this[metaKey] = {
            page: typeof p.page === 'number' ? p.page + 1 : 1,
            pages: p.pages || 1,
            count: p.count || 0,
          };
          this[`${view}Loaded`] = true;
        })
        .catch(() => {
          this[listKey] = [];
          this[metaKey] = { page: 1, pages: 1, count: 0 };
        })
        .finally(() => { this[loadingKey] = false; });
    },
    isMineRequester(r) {
      return Number(r.requested_by_user_id) === Number(this.me);
    },
    mineOtherName(r) {
      const other = this.isMineRequester(r) ? r.target_user : r.requested_by;
      return (other && other.name) || '—';
    },
    mineOwnSchedule(r) {
      return this.isMineRequester(r) ? r.work_schedule : r.target_work_schedule;
    },
    mineOtherSchedule(r) {
      return this.isMineRequester(r) ? r.target_work_schedule : r.work_schedule;
    },
    goToTabPage(view, page) {
      this[`${view}Meta`].page = page;
      this.fetchTabList(view);
    },
    refreshTabLists() {
      if (this.receivedLoaded) this.fetchTabList('received');
      if (this.acceptedLoaded) this.fetchTabList('accepted');
      if (this.mineLoaded) this.fetchTabList('mine');
      if (this.approvalsLoaded) this.fetchTabList('approvals');
    },
    toggleEditTimes() {
      this.editTimes = !this.editTimes;
      if (this.editTimes) {
        this.form.source_shift_start_time = this.toInputDt(this.selectedMine && this.selectedMine.shift_start_time);
        this.form.source_shift_end_time = this.toInputDt(this.selectedMine && this.selectedMine.shift_end_time);
        this.form.target_shift_start_time = this.toInputDt(this.selectedTheirs && this.selectedTheirs.shift_start_time);
        this.form.target_shift_end_time = this.toInputDt(this.selectedTheirs && this.selectedTheirs.shift_end_time);
      }
    },
    toggleApprovalEdit(r) {
      if (this.approvalEditId === r.id) {
        this.approvalEditId = null;
        return;
      }
      this.$set(this.approvalEdit, r.id, {
        source_shift_start_time: this.toInputDt(r.work_schedule && r.work_schedule.shift_start_time),
        source_shift_end_time: this.toInputDt(r.work_schedule && r.work_schedule.shift_end_time),
        target_shift_start_time: this.toInputDt(r.target_work_schedule && r.target_work_schedule.shift_start_time),
        target_shift_end_time: this.toInputDt(r.target_work_schedule && r.target_work_schedule.shift_end_time),
      });
      this.approvalEditId = r.id;
    },
    onDirectUserAChange() {
      this.directForm.work_schedule_id = '';
      if (Number(this.directForm.user_b) === Number(this.directForm.user_a)) {
        this.directForm.user_b = null;
        this.directForm.target_work_schedule_id = '';
      }
    },
    onDirectUserBChange() {
      this.directForm.target_work_schedule_id = '';
    },
    toggleDirectA(id) {
      this.directForm.work_schedule_id = this.directForm.work_schedule_id === id ? '' : id;
    },
    toggleDirectB(id) {
      this.directForm.target_work_schedule_id = this.directForm.target_work_schedule_id === id ? '' : id;
    },
    toggleDirectEditTimes() {
      this.directEditTimes = !this.directEditTimes;
      if (this.directEditTimes) {
        this.directForm.source_shift_start_time = this.toInputDt(this.directSelectedA && this.directSelectedA.shift_start_time);
        this.directForm.source_shift_end_time = this.toInputDt(this.directSelectedA && this.directSelectedA.shift_end_time);
        this.directForm.target_shift_start_time = this.toInputDt(this.directSelectedB && this.directSelectedB.shift_start_time);
        this.directForm.target_shift_end_time = this.toInputDt(this.directSelectedB && this.directSelectedB.shift_end_time);
      }
    },
    resetDirectForm() {
      this.directForm.work_schedule_id = '';
      this.directForm.target_work_schedule_id = '';
      this.directForm.requested_reason = '';
      this.directForm.source_shift_start_time = '';
      this.directForm.source_shift_end_time = '';
      this.directForm.target_shift_start_time = '';
      this.directForm.target_shift_end_time = '';
      this.directForm.notify = false;
      this.directEditTimes = false;
    },
    submitDirect() {
      this.loading = true;
      axios.post(this.api('work_schedule_swap_requests/direct'), {
        work_schedule_id: this.directForm.work_schedule_id || null,
        target_work_schedule_id: this.directForm.target_work_schedule_id || null,
        requested_reason: this.directForm.requested_reason || null,
        source_shift_start_time: this.directForm.source_shift_start_time || null,
        source_shift_end_time: this.directForm.source_shift_end_time || null,
        target_shift_start_time: this.directForm.target_shift_start_time || null,
        target_shift_end_time: this.directForm.target_shift_end_time || null,
        notify: this.directForm.notify,
      }).then(() => {
        this.$message('Sucesso', 'Troca realizada.', 'success');
        this.resetDirectForm();
        this.loadMonth();
        this.fetchSwaps();
      }).catch(e => {
        this.$message('Erro', e?.response?.data?.message || 'Erro ao registrar a troca.', 'error');
      }).finally(() => { this.loading = false; });
    },
    submit() {
      this.loading = true;
      axios.post(this.api('work_schedule_swap_requests'), {
        work_schedule_id: this.form.work_schedule_id || null,
        target_work_schedule_id: this.form.target_work_schedule_id || null,
        requested_reason: this.form.requested_reason || null,
        source_shift_start_time: this.form.source_shift_start_time || null,
        source_shift_end_time: this.form.source_shift_end_time || null,
        target_shift_start_time: this.form.target_shift_start_time || null,
        target_shift_end_time: this.form.target_shift_end_time || null,
      }).then(() => {
        this.$message('Sucesso', 'Solicitação enviada.', 'success');
        this.form.work_schedule_id = '';
        this.form.target_work_schedule_id = '';
        this.form.requested_reason = '';
        this.form.source_shift_start_time = '';
        this.form.source_shift_end_time = '';
        this.form.target_shift_start_time = '';
        this.form.target_shift_end_time = '';
        this.editTimes = false;
        this.fetchSwaps();
      }).catch(e => {
        this.$message('Erro', e?.response?.data?.message || 'Erro ao enviar solicitação.', 'error');
      }).finally(() => { this.loading = false; });
    },
    accept(id) {
      this.loading = true;
      axios.post(this.api(`work_schedule_swap_requests/${id}/accept`))
        .then(() => { this.$message('Sucesso', 'Troca aceita. Aguardando aprovação.', 'success'); this.fetchSwaps(); this.$store.dispatch('pending/fetch'); })
        .catch(e => this.$message('Erro', e?.response?.data?.message || 'Erro ao aceitar.', 'error'))
        .finally(() => { this.loading = false; });
    },
    decline(id) {
      this.loading = true;
      axios.post(this.api(`work_schedule_swap_requests/${id}/decline`))
        .then(() => { this.$message('Sucesso', 'Troca recusada.', 'success'); this.fetchSwaps(); this.$store.dispatch('pending/fetch'); })
        .catch(e => this.$message('Erro', e?.response?.data?.message || 'Erro ao recusar.', 'error'))
        .finally(() => { this.loading = false; });
    },
    approve(id) {
      this.loading = true;
      const edit = this.approvalEdit[id] || {};
      axios.post(this.api(`work_schedule_swap_requests/${id}/approve`), {
        source_shift_start_time: edit.source_shift_start_time || null,
        source_shift_end_time: edit.source_shift_end_time || null,
        target_shift_start_time: edit.target_shift_start_time || null,
        target_shift_end_time: edit.target_shift_end_time || null,
      })
        .then(() => { this.$message('Sucesso', 'Troca aprovada.', 'success'); this.approvalEditId = null; this.fetchSwaps(); this.$store.dispatch('pending/fetch'); })
        .catch(e => this.$message('Erro', e?.response?.data?.message || 'Erro ao aprovar.', 'error'))
        .finally(() => { this.loading = false; });
    },
    reject(id) {
      const input = document.createElement('input');
      input.type = 'text';
      input.maxLength = 1000;
      input.placeholder = 'Justificativa da reprovação (obrigatória)';
      input.className = 'form-control';

      swal({
        title: 'Reprovar troca',
        text: 'Informe a justificativa da reprovação:',
        icon: 'warning',
        content: input,
        buttons: ['Cancelar', 'Reprovar'],
        dangerMode: true,
      }).then((confirmed) => {
        if (!confirmed) return;
        const reason = (input.value || '').trim();
        if (!reason) {
          this.$message('Atenção', 'A justificativa é obrigatória.', 'warning');
          return;
        }
        this.loading = true;
        axios.post(this.api(`work_schedule_swap_requests/${id}/reject`), { approver_justification: reason })
          .then(() => { this.$message('Sucesso', 'Troca reprovada.', 'success'); this.fetchSwaps(); this.$store.dispatch('pending/fetch'); })
          .catch(e => this.$message('Erro', e?.response?.data?.message || 'Erro ao reprovar.', 'error'))
          .finally(() => { this.loading = false; });
      });
    },
  },
};
</script>

<style scoped>
.swap-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  border-bottom: 1px solid #e6e6ef;
  padding-bottom: 0.5rem;
  margin-bottom: 1rem;
  list-style: none;
  padding-left: 0;
}
.swap-tab-item { margin: 0; }
.swap-tab {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid transparent;
  background: transparent;
  color: #6e6b7b;
  font-weight: 600;
  font-size: 0.88rem;
  border-radius: 8px;
  padding: 0.45rem 0.8rem;
  cursor: pointer;
  transition: all 0.15s ease;
}
.swap-tab:hover { background: #eaf1fa; color: #15488A; }
.swap-tab.active {
  background: #15488A;
  color: #fff;
  box-shadow: 0 4px 10px rgba(21, 72, 138, 0.35);
}
.swap-tab-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: #ea5455;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1;
}
.swap-tab-badge.soft { background: #b9b9c3; }
.swap-tab-badge.danger { background: #ea5455; }
.swap-tab.active .swap-tab-badge { background: #fff; color: #15488A; }

.swap-page { min-height: 340px; }
.swap-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 340px;
  color: #6e6b7b;
  font-size: 0.95rem;
}
.swap-pane { animation: swapFade 0.15s ease; }
@keyframes swapFade {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}
.pane-title {
  font-weight: 600;
  color: #5e5873;
  margin-bottom: 0.5rem;
}

.swap-step-head {
  display: flex;
  align-items: center;
  font-weight: 600;
  color: #4b4b6b;
  margin: 0.25rem 0 0.75rem;
}
.step-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #15488A;
  color: #fff;
  font-size: 0.75rem;
  margin-right: 0.5rem;
}

.swap-grid {
  display: grid;
  grid-template-columns: 1fr 48px 1fr;
  align-items: start;
  gap: 0.5rem;
}
.swap-col-title { font-weight: 600; margin-bottom: 0.5rem; color: #5e5873; }
.swap-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #b9b9c3;
  font-size: 1.25rem;
  padding-top: 2.5rem;
}

.sched-list { display: flex; flex-direction: column; gap: 0.4rem; max-height: 240px; overflow-y: auto; }
.sched-card {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  width: 100%;
  text-align: left;
  background: #fff;
  border: 1px solid #e6e6ef;
  border-radius: 0.5rem;
  padding: 0.55rem 0.75rem;
  cursor: pointer;
  transition: all 0.15s ease;
}
.sched-card:hover { border-color: #15488A; background: #eef4fb; }
.sched-card.selected { border-color: #15488A; background: #e3edf8; box-shadow: 0 0 0 1px #15488A inset; }
.sched-card:disabled,
.sched-card.locked { opacity: 0.6; cursor: not-allowed; background: #f4f4f8; }
.sched-card:disabled:hover,
.sched-card.locked:hover { border-color: #e6e6ef; background: #f4f4f8; }
.sched-pending {
  display: inline-flex;
  align-items: center;
  font-size: 0.68rem;
  font-weight: 700;
  color: #b06a00;
  background: #fbf1df;
  border: 1px solid #f0d68a;
  border-radius: 10px;
  padding: 1px 7px;
  white-space: nowrap;
}
.sched-ico { color: #15488A; font-size: 1.1rem; }
.sched-info { display: flex; flex-direction: column; line-height: 1.1; flex: 1; }
.sched-date { font-weight: 600; color: #5e5873; }
.sched-time { font-size: 0.78rem; color: #6e6b7b; }
.sched-on { color: #28c76f; }

.swap-range { color: #272727; font-size: 0.78rem; }
td .swap-range { display: block; }
.swap-approval-edit > td { background: #f7f9fc; border-top: 0; }
.dark-layout .swap-range { color: #d4d4d4; font-size: 0.78rem; }

.swap-history-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}
.swap-history-table th { vertical-align: middle; }
.swap-filter-row th {
  padding: 0.35rem 0.5rem 0.5rem;
  background: #f7f9fc;
  border-top: 0;
  font-weight: 400;
}
.swap-filter-row .form-control-sm { min-width: 7rem; }
.swap-date-input {
  min-width: 0;
  max-width: 100%;
}

.swap-empty {
  color: #a0a0b2;
  font-size: 0.85rem;
  border: 1px dashed #e0e0ec;
  border-radius: 0.5rem;
  padding: 0.75rem;
  text-align: center;
}

.summary-bar {
  background: #eafaf1;
  border: 1px solid #c3ecd5;
  color: #1f7a4d;
  border-radius: 0.5rem;
  padding: 0.6rem 0.85rem;
  font-size: 0.9rem;
}

.swap-hint {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
  background: #eef4fb;
  border: 1px solid #cfe0f2;
  color: #2c4a6b;
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.82rem;
  line-height: 1.45;
}
.swap-hint .sched-pending { margin: 0 0.15rem; }

.recv-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  border: 1px solid #e6e6ef;
  border-radius: 0.5rem;
  padding: 0.6rem 0.85rem;
  margin-bottom: 0.5rem;
}
.recv-actions { white-space: nowrap; }

.swap-actions-cell {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 0.6rem;
  justify-content: center;
}

@media (max-width: 767.98px) {
  .swap-grid { grid-template-columns: 1fr; }
  .swap-arrow { transform: rotate(90deg); padding: 0.25rem 0; }
  .recv-card { flex-direction: column; align-items: stretch; }
  .swap-actions-cell { flex-direction: column; align-items: stretch; }
}
</style>
